import { useEffect } from 'react';
import { Box, Button, Modal, Paper } from '@mui/material';
import useBattleshipModalStyles from './useBattleshipModalStyles';
import BattleshipButton from './battleshipButton';
import useGameModal from '../../../../hooks/useGameModal';
import useGlobalStyles from '../../../useGlobalStyles';
import { useAppDispatch } from '../../../../redux/hooks';
import { addOneToScore, updateUserFields } from '../../../../redux/slices/userSlice';

/**
 * Interface representing the props for the Battleship Modal component.
 *
 * - open Boolean representing whether or not the modal is open.
 * - closeModal The function to close the modal.
 */
interface BattleshipModalProps {
  open: boolean;
  closeModal: () => void;
}

/**
 * Battleship Modal component renders a battleship game board containing one area
 * with your own ships, and another where you can guess the location of an opponents
 * ships.
 *
 * @param open Boolean representing whether or not the modal is open.
 * @param closeModal The function to close the modal.
 */
const BattleshipModal = ({ open, closeModal }: BattleshipModalProps) => {
  const { classes } = useBattleshipModalStyles();
  const { classes: globalClasses } = useGlobalStyles();
  const dispatch = useAppDispatch();
  const {
    selfId,
    opponent,
    gameStatus,
    gameResult,
    gameType,
    isChallenge,
    oppId,
    battleshipHits,
    canPlay,
    gameOver,
    statusText,
    gameOverText,
    collectPoint,
    hideBoard,
    handleExit,
  } = useGameModal();

  useEffect(() => {
    if (gameOver && gameResult[0] === 'won' && isChallenge && gameType === 'battleship') {
      if (gameResult[0] === 'won' && isChallenge) {
        dispatch(addOneToScore());
        dispatch(updateUserFields({ uid: selfId, field: 'score', updatedValue: '1' }));
      }
    }
  }, [dispatch, gameOver, gameResult, gameType, isChallenge, selfId]);

  /**
   * Function to create a grid of Battleship Buttons that make up the game boards.
   *
   * @param type - The type of grid to create, either the one displaying your own ships
   * or the one where you can make guesses on the locations of an opponent's ships.
   * @param idx - The number of the grid being created to store in the key attribute.
   * @returns the grid of Battleship Buttons.
   */
  const createGrid = (type: string, idx: number) => (
    <div className={classes.gridContainer} key={idx}>
      <span style={{ padding: '10px' }}>{type === 'self' ? 'Your boats' : 'Opponents boats'}</span>
      <div className={classes.buttonRows}>
        <div className={`${!canPlay && classes.overlay}`}></div>
        {[...Array(10).keys()]
          .map(i => i * 10)
          .map(row => (
            <div className={classes.buttonColumns} key={row}>
              {[...Array(10).keys()]
                .map(i => i + row)
                .map(num => (
                  <BattleshipButton
                    key={num}
                    idx={num}
                    selfId={selfId}
                    oppId={oppId}
                    gameStatus={gameStatus}
                    canPlay={canPlay}
                    type={type}
                    hits={battleshipHits}
                  />
                ))}
            </div>
          ))}
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={() => {}}>
      <Box className={classes.modalStyles} component={Paper}>
        {!hideBoard ? (
          <>
            <span className={`${globalClasses.h3} ${classes.titleText}`}>Battleship</span>
            <span className={classes.playingAgainst}>
              Playing against: <span style={{ fontWeight: 600 }}>{opponent?.username || ''}</span>
            </span>
            <span className={classes.status}>{statusText}</span>
            <div className={classes.gridsContainer}>
              {['self', 'opponent'].map((type, idx) => createGrid(type, idx))}
            </div>
          </>
        ) : (
          <span className={classes.status}>{gameOverText || statusText}</span>
        )}
        <Button
          className={`${globalClasses.button} ${classes.quit} ${collectPoint && classes.win}`}
          onClick={() => handleExit(gameOver ? 'exit' : 'quit', closeModal)}>
          {collectPoint ? 'Collect 1 point!' : 'Exit'}
        </Button>
      </Box>
    </Modal>
  );
};

export default BattleshipModal;
